import React, { useState, useRef, useEffect } from 'react';
import { 
  Stack, 
  IconButton, 
  Tooltip, 
  Text, 
  DefaultButton, 
  Dialog, 
  DialogFooter, 
  DialogContent, 
  PrimaryButton,
  mergeStyleSets,
  IIconProps,
  TooltipHost,
  DialogType,
  Dropdown,
  IDropdownOption
} from '@fluentui/react';
import { 
  DocumentRegular, 
  AddRegular, 
  DismissRegular,
  DocumentPdfRegular,
  DocumentTextRegular,
  DocumentTableRegular,
  ImageRegular,
  DocumentDataRegular,
  ChevronDownRegular,
  ChevronUpRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import { buildApiUrl } from '../../config/apiConfig';
import { useAsyncUpload } from '../../hooks/useAsyncUpload';
import { openFile as smartOpenFile } from '../../utils/fileOpenUtils';
import { UploadProgressIndicator } from '../UploadProgress/UploadProgressIndicator';
import styles from './DocumentManager.module.css';

interface UploadedDocument {
  id: string;
  fileId?: string; // For async upload tracking
  filename: string;
  type: 'document' | 'image' | 'text' | 'spreadsheet';
  size?: number;
  uploadDate?: string;
  preview?: string;
  uploading?: boolean;
  uploadProgress?: number;
  processing?: boolean; // True if file is being processed after upload
  processingProgress?: number; // Processing progress (0-100)
  processingStatus?: string; // e.g., 'queued', 'processing', 'completed', 'failed'
  uploaded?: boolean; // True if processing completed successfully
  error?: string; // Error message if processing failed
  // Excel multi-sheet support
  available_sheets?: Array<{ name: string; rows: number; columns: number; column_names?: string[] }>;
  sheet_name?: string; // Currently selected sheet
}

interface DocumentManagerProps {
  documents: UploadedDocument[];
  onDocumentsChange: (documents: UploadedDocument[]) => void;
  onAddDocument: () => void;
  disabled?: boolean;
  maxDocuments?: number;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  compact?: boolean; // New prop for horizontal compact layout
  isUploading?: boolean; // External upload state
  uploadingFilesCount?: number; // Number of files being uploaded
}

// Helper function to get appropriate icon for file type
const getFileIcon = (filename: string, type?: string): React.ReactElement => {
  const extension = filename.toLowerCase().split('.').pop() || '';
  
  switch (extension) {
    case 'pdf':
      return <DocumentPdfRegular className={styles.fileIcon} data-type="document" />;
    case 'doc':
    case 'docx':
      return <DocumentTextRegular className={styles.fileIcon} data-type="document" />;
    case 'xls':
    case 'xlsx':
    case 'csv':
      return <DocumentTableRegular className={styles.fileIcon} data-type="spreadsheet" />;
    case 'txt':
    case 'md':
    case 'rtf':
      return <DocumentDataRegular className={styles.fileIcon} data-type="text" />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'webp':
    case 'svg':
      return <ImageRegular className={styles.fileIcon} data-type="image" />;
    default:
      return <DocumentRegular className={styles.fileIcon} data-type="document" />;
  }
};

// Helper function to format file size
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
};

// Helper function to open uploaded file
const openFile = async (doc: UploadedDocument) => {
  if (!doc.fileId && !doc.id) return;
  
  try {
    // Get download URL from backend
    const fileId = doc.fileId || doc.id;
    const response = await authenticatedFetch(`/document/${fileId}/download`);
    if (!response.ok) {
      console.error('Failed to get download URL');
      return;
    }
    const data = await response.json();
    let url = data.download_url;
    
    // Build full API URL
    if (url.startsWith('/api/')) {
      url = buildApiUrl(url);
    }
    
    // Use smart file opening (Office files in O365 viewer, PDFs inline, etc.)
    smartOpenFile(url, doc.filename);
  } catch (error) {
    console.error('Error opening file:', error);
  }
};

export const DocumentManager: React.FC<DocumentManagerProps> = ({
  documents,
  onDocumentsChange,
  onAddDocument,
  disabled = false,
  maxDocuments = 10,
  collapsible = false,
  defaultCollapsed = false,
  compact = false,
  isUploading: externalIsUploading = false,
  uploadingFilesCount = 0
}) => {
  const [showRemoveDialog, setShowRemoveDialog] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  
  // Initialize async upload hook
  const { startUpload, getAllUploads, getUploadProgress, removeUpload, cleanup } = useAsyncUpload();
  
  // Use external uploading state if provided, otherwise use internal state
  const actualIsUploading = externalIsUploading || isUploading;

  // Cleanup async uploads on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Helper function to upload a file and return document metadata
  const uploadFile = async (file: File): Promise<UploadedDocument | null> => {
    try {
      const fileId = await startUpload(file);
      
      if (!fileId) {
        console.error('Failed to start async upload');
        return null;
      }

      // Convert file info to UploadedDocument format
      const extension = file.name.toLowerCase().split('.').pop() || '';
      let type: 'document' | 'image' | 'text' | 'spreadsheet' = 'document';
      
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
        type = 'image';
      } else if (['txt', 'md', 'rtf'].includes(extension)) {
        type = 'text';
      } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
        type = 'spreadsheet';
      }

      return {
        id: fileId,
        fileId: fileId,
        filename: file.name,
        type: type,
        size: file.size,
        uploadDate: new Date().toISOString(),
        uploading: true,
        uploadProgress: 0
      };
    } catch (error) {
      console.error('Error starting upload:', error);
      return null;
    }
  };

  const handleRemoveDocument = async (documentId: string) => {
    setIsRemoving(documentId);
    
    try {
      // Find the document to get fileId if it has one
      const document = documents.find(doc => doc.id === documentId);
      
      // Clean up async upload tracking if document has fileId
      if (document?.fileId) {
        removeUpload(document.fileId);
      }
      
      // Call backend to remove document if needed
      // For now, just remove from local state
      const updatedDocuments = documents.filter(doc => doc.id !== documentId);
      onDocumentsChange(updatedDocuments);
      
      setShowRemoveDialog(null);
    } catch (error) {
      console.error('Error removing document:', error);
    } finally {
      setIsRemoving(null);
    }
  };

  const confirmRemoveDocument = (documentId: string) => {
    setShowRemoveDialog(documentId);
  };

  const cancelRemoveDocument = () => {
    setShowRemoveDialog(null);
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && documents.length < maxDocuments && !isUploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled || documents.length >= maxDocuments || isUploading) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Check if we would exceed the limit
    const availableSlots = maxDocuments - documents.length;
    const filesToUpload = files.slice(0, availableSlots);
    
    if (filesToUpload.length === 0) return;

    setIsUploading(true);
    
    try {
      const uploadPromises = filesToUpload.map(file => uploadFile(file));
      const uploadResults = await Promise.all(uploadPromises);
      
      // Filter out failed uploads
      const successfulUploads = uploadResults.filter((result): result is UploadedDocument => result !== null);
      
      if (successfulUploads.length > 0) {
        const updatedDocuments = [...documents, ...successfulUploads];
        onDocumentsChange(updatedDocuments);
      }
      
      // Show feedback if some uploads failed
      if (successfulUploads.length < filesToUpload.length) {
        console.warn(`${filesToUpload.length - successfulUploads.length} file(s) failed to upload`);
      }
      
    } catch (error) {
      console.error('Error during drag and drop upload:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const documentToRemove = documents.find(doc => doc.id === showRemoveDialog);

  // Compact horizontal layout
  if (compact) {
    return (
      <div className={styles.compactContainer}>
        {/* Horizontal document list */}
        <div className={styles.compactDocumentList}>
          {documents.map((doc, index) => {
            // Use status directly from document props (passed from parent's sync effect)
            // Don't rely on local useAsyncUpload since uploads are tracked by parent
            const isProcessing = doc.processing || doc.uploading;
            const isCompleted = doc.uploaded && !doc.uploading && !doc.processing;
            const isFailed = !!doc.error;
            
            return (
              <div 
                key={doc.id} 
                className={`${styles.compactDocumentItem} ${isProcessing ? styles.processing : ''} ${isFailed ? styles.failed : ''}`}
                onDoubleClick={() => isCompleted && openFile(doc)}
                style={{ cursor: isCompleted ? 'pointer' : 'default' }}
                title={isCompleted ? 'Double-click to open' : undefined}
              >
                {/* Processing indicator when file is being processed */}
                {isProcessing && (
                  <div className={styles.processingIndicator}>
                    <span className={styles.processingSpinner}>⏳</span>
                    <span className={styles.processingText}>
                      {doc.processingStatus === 'queued' ? 'Queued' : 
                       doc.processingStatus === 'uploading' ? 'Uploading' : 'Processing'}
                      {doc.processingProgress ? ` ${doc.processingProgress}%` : ''}
                    </span>
                  </div>
                )}
                
                <div className={styles.compactIconContainer}>
                  {isCompleted && <span className={styles.completedBadge}>✓</span>}
                  {isFailed && <span className={styles.failedBadge}>✗</span>}
                  {getFileIcon(doc.filename)}
                </div>
                <span className={styles.compactFileName} title={doc.filename}>
                  {doc.filename.length > 15 ? `${doc.filename.substring(0, 15)}...` : doc.filename}
                </span>
                
                {/* Sheet selector for Excel files with multiple sheets */}
                {doc.available_sheets && doc.available_sheets.length > 1 && isCompleted && (
                  <Dropdown
                    placeholder="Sheet"
                    selectedKey={doc.sheet_name || doc.available_sheets[0]?.name}
                    options={doc.available_sheets.map(sheet => ({
                      key: sheet.name,
                      text: sheet.name,
                    }))}
                    onChange={(_ev, option) => {
                      if (option) {
                        const updatedDocs = documents.map(d => 
                          d.id === doc.id ? { ...d, sheet_name: option.key as string } : d
                        );
                        onDocumentsChange(updatedDocs);
                      }
                    }}
                    styles={{
                      root: { minWidth: 90, maxWidth: 130, marginLeft: 4 },
                      dropdown: { 
                        borderRadius: 6,
                        border: '1px solid #e0e0e0',
                        ':hover': { borderColor: '#0078d4' },
                        ':focus': { borderColor: '#0078d4' }
                      },
                      title: { 
                        fontSize: 12, 
                        height: 26, 
                        lineHeight: '26px', 
                        padding: '0 28px 0 10px',
                        borderRadius: 6,
                        backgroundColor: '#f8f9fa',
                        fontWeight: 500
                      },
                      dropdownItem: { 
                        fontSize: 12, 
                        minHeight: 28,
                        padding: '0 10px'
                      },
                      dropdownItemSelected: {
                        backgroundColor: '#e6f2ff'
                      },
                      caretDownWrapper: { 
                        height: 26, 
                        lineHeight: '26px',
                        right: 8
                      },
                      caretDown: {
                        fontSize: 10
                      }
                    }}
                  />
                )}
                
                <TooltipHost content="Remove document">
                  <IconButton
                    iconProps={{ iconName: 'Cancel' }}
                    title="Remove document"
                    ariaLabel={`Remove ${doc.filename}`}
                    onClick={() => confirmRemoveDocument(doc.id)}
                    disabled={disabled || isRemoving === doc.id}
                    className={styles.compactRemoveButton}
                  />
                </TooltipHost>
              </div>
            );
          })}
          
          {/* Add button */}
          {documents.length < maxDocuments && !actualIsUploading && (
            <TooltipHost content="Add document">
              <IconButton
                iconProps={{ iconName: 'Add' }}
                title="Add document"
                ariaLabel="Add document"
                onClick={onAddDocument}
                disabled={disabled}
                className={styles.compactAddButton}
              />
            </TooltipHost>
          )}
          
          {/* Upload status */}
          {actualIsUploading && (
            <div className={styles.compactUploadStatus}>
              <div className={styles.uploadSpinner}>📄</div>
              <Text variant="small">
                Uploading {uploadingFilesCount > 0 ? `${uploadingFilesCount} file${uploadingFilesCount > 1 ? 's' : ''}` : 'files'}...
              </Text>
            </div>
          )}
        </div>

        {/* Remove Confirmation Dialog */}
        <Dialog
          hidden={!showRemoveDialog}
          onDismiss={cancelRemoveDocument}
          dialogContentProps={{
            type: DialogType.normal,
            title: 'Remove Document',
            subText: `Are you sure you want to remove "${documentToRemove?.filename}"? This action cannot be undone.`
          }}
        >
          <DialogFooter>
            <PrimaryButton
              onClick={() => showRemoveDialog && handleRemoveDocument(showRemoveDialog)}
              text="Remove"
              disabled={isRemoving !== null}
            />
            <DefaultButton 
              onClick={cancelRemoveDocument} 
              text="Cancel"
              disabled={isRemoving !== null}
            />
          </DialogFooter>
        </Dialog>
      </div>
    );
  }

  // Original vertical layout
  return (
    <div 
      className={`${styles.documentManager} ${isDragOver ? styles.dragOver : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div className={styles.header}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <DocumentRegular className={styles.headerIcon} />
          <Text variant="medium" className={styles.headerText}>
            Documents ({documents.length})
          </Text>
          {collapsible && (
            <TooltipHost content={isCollapsed ? "Expand documents" : "Collapse documents"}>
              <IconButton
                iconProps={{ iconName: isCollapsed ? 'ChevronDown' : 'ChevronUp' }}
                title={isCollapsed ? "Expand documents" : "Collapse documents"}
                ariaLabel={isCollapsed ? "Expand documents" : "Collapse documents"}
                onClick={() => setIsCollapsed(!isCollapsed)}
                className={styles.collapseButton}
              />
            </TooltipHost>
          )}
          {documents.length < maxDocuments && !isUploading && !isCollapsed && (
            <TooltipHost content="Add document">
              <IconButton
                iconProps={{ iconName: 'Add' }}
                title="Add document"
                ariaLabel="Add document"
                onClick={onAddDocument}
                disabled={disabled}
                className={styles.addButton}
              />
            </TooltipHost>
          )}
        </Stack>
        {isUploading && (
          <Text variant="tiny" className={styles.uploadingText}>
            Uploading files...
          </Text>
        )}
      </div>

      {/* Document List */}
      {!isCollapsed && documents.length > 0 && (
        <div className={styles.documentList}>
          {documents.map((doc) => {
            // Use status directly from document props (passed from parent's sync effect)
            const isProcessing = doc.processing || doc.uploading;
            
            return (
              <div key={doc.id} className={`${styles.documentItem} ${isProcessing ? styles.processing : ''}`}>
                {/* Show processing indicator if file is still being processed */}
                {isProcessing && (
                  <div className={styles.uploadProgressSection}>
                    <div className={styles.processingIndicator}>
                      <span className={styles.processingSpinner}>⏳</span>
                      <span className={styles.processingText}>
                        {doc.processingStatus === 'queued' ? 'Queued' : 
                         doc.processingStatus === 'uploading' ? 'Uploading' : 'Processing'}
                        {doc.processingProgress ? ` ${doc.processingProgress}%` : ''}
                      </span>
                    </div>
                  </div>
                )}
                
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                  {/* File Icon */}
                  <div className={styles.iconContainer}>
                    {getFileIcon(doc.filename)}
                  </div>
                  
                  {/* File Info */}
                  <Stack className={styles.fileInfo}>
                    <Text variant="small" className={styles.filename} title={doc.filename}>
                      {doc.filename}
                    </Text>
                    {doc.size && (
                      <Text variant="tiny" className={styles.fileSize}>
                        {formatFileSize(doc.size)}
                      </Text>
                    )}
                  </Stack>

                  {/* Remove Button */}
                  <TooltipHost content="Remove document">
                    <IconButton
                      iconProps={{ iconName: 'Cancel' }}
                      title="Remove document"
                      ariaLabel={`Remove ${doc.filename}`}
                      onClick={() => confirmRemoveDocument(doc.id)}
                      disabled={disabled || isRemoving === doc.id}
                      className={styles.removeButton}
                    />
                  </TooltipHost>
                </Stack>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!isCollapsed && documents.length === 0 && (
        <div className={styles.emptyState}>
          <DocumentRegular className={styles.emptyIcon} />
          <Text variant="small" className={styles.emptyText}>
            {isUploading ? 'Uploading files...' : isDragOver ? 'Drop files here' : 'No documents uploaded'}
          </Text>
          <Text variant="tiny" className={styles.emptySubText}>
            {isUploading ? 'Please wait...' : isDragOver ? '' : 'Drag & drop files or click to add'}
          </Text>
          {!isUploading && (
            <DefaultButton
              text="Add Document"
              iconProps={{ iconName: 'Add' }}
              onClick={onAddDocument}
              disabled={disabled}
              className={styles.emptyAddButton}
            />
          )}
        </div>
      )}

      {/* Remove Confirmation Dialog */}
      <Dialog
        hidden={!showRemoveDialog}
        onDismiss={cancelRemoveDocument}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Remove Document',
          subText: `Are you sure you want to remove "${documentToRemove?.filename}"? This action cannot be undone.`
        }}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={() => showRemoveDialog && handleRemoveDocument(showRemoveDialog)}
            text="Remove"
            disabled={isRemoving !== null}
          />
          <DefaultButton 
            onClick={cancelRemoveDocument} 
            text="Cancel"
            disabled={isRemoving !== null}
          />
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default DocumentManager;